import React from "react";
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";
import Button from "components/Common/Button";
import Banner from "../LiveAgents/SalesforceLAModal/Banner";
import InputRow from "../LiveAgents/SalesforceLAModal/InputRow";

const INVALID_CREDENTIALS_ERROR_MESSAGE = "We couldn’t connect to your account. Verify the highlighted fields and try again.";
const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";

const SettingsScreen = ({
  platform,
  saveClientPlatform,
  updateZendeskAgentWorkspacePlatform,
  isSaveDisabled,
  closeModal,
  modalRef,
  errorInfo
}) => {
  let errorBanner;
  let invalidSunshineCredentials;
  let invalidZendeskCredentials;

  if (errorInfo) {
    let message;

    if (errorInfo.type === "validation_exception") {
      invalidSunshineCredentials = errorInfo.platform === "sunshine";
      invalidZendeskCredentials = errorInfo.platform === "zendesk";
      message = INVALID_CREDENTIALS_ERROR_MESSAGE;
    } else {
      message = GENERIC_ERROR_MESSAGE;
    }

    errorBanner = (
      <Banner icon="Error" intent="negative">
        {message}
      </Banner>
    );
  }

  return (
    <>
      <div className="Modal__content ZendeskAgentWorkspace__Settings">
        <Banner icon="QuestionCircleFilled">
          Need help?{" "}
          {/** TODO: Add actual link */}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="" target="_blank" rel="noopener noreferrer">
            Open Sunshine Conversations Guide
          </a>{" "}
          to assist you in completing this setup.
        </Banner>
        {errorBanner}
        <section className="Modal__section">
          <InputRow
            // N.B.: "split" rows get an extra class name:
            className="ZendeskAgentWorkspace__Settings__cell"
            label="App ID"
            tooltip={`App ID can be found under the “Settings”
          in your Sunshine Conversations app.`}
            value={platform.getIn(["record", "auth", "appId"])}
            placeholder="e.g 5cd9d0d0145aa200102844b6"
            containerRef={modalRef.current}
            onChange={
              e => updateZendeskAgentWorkspacePlatform({ auth: { appId: e.target.value } })
            }
            invalid={invalidSunshineCredentials}
          />
          <InputRow
            // N.B.: "split" rows get an extra class name:
            className="ZendeskAgentWorkspace__Settings__cell"
            label="API Key"
            tooltip={`API Key can be found under the “Settings”
          in your Sunshine Conversations app.`}
            value={platform.getIn(["record", "auth", "apiKey"])}
            placeholder="app_5cfa68012430b67758317c08"
            containerRef={modalRef.current}
            onChange={
              e => updateZendeskAgentWorkspacePlatform({ auth: { apiKey: e.target.value } })
            }
            invalid={invalidSunshineCredentials}
          />
          <InputRow
            // N.B.: "split" rows get an extra class name:
            className="ZendeskAgentWorkspace__Settings__cell"
            label="API Secret"
            tooltip={`API Secret can be found under the “Settings”
          in your Sunshine Conversations app.`}
            value={platform.getIn(["record", "auth", "apiSecret"])}
            placeholder="e.g mDhqqgwwtDXAZTGNyPOhGrCVd6HjrhxL6cPevQZTX7-5oECtQ6iK5uGHn1md
eoDriAZtyY4SkIgybNRpGiz2Tg"
            containerRef={modalRef.current}
            onChange={
              e => updateZendeskAgentWorkspacePlatform({ auth: { apiSecret: e.target.value } })
            }
            invalid={invalidSunshineCredentials}
          />
          <div className="ZendeskAgentWorkspace__Settings__zd-social__row">
            <InputRow
              // N.B.: "split" rows get an extra class name:
              className="ZendeskAgentWorkspace__Settings__cell"
              label="Zendesk Subdomain"
              tooltip={`Your subdomain is the first part of your helpdesk URL: 
              https://[yoursubdomain].zendesk.com`}
              value={platform.getIn(["record", "auth", "subdomain"])}
              placeholder="e.g Mycompany"
              containerRef={modalRef.current}
              onChange={
                e => updateZendeskAgentWorkspacePlatform({ auth: { subdomain: e.target.value } })
              }
              invalid={invalidZendeskCredentials}
            />
            <InputRow
              // N.B.: "split" rows get an extra class name:
              className="ZendeskAgentWorkspace__Settings__cell"
              label="Zendesk Login Email"
              tooltip="The email you use to login to Zendesk"
              value={platform.getIn(["record", "auth", "email"])}
              placeholder="e.g you@yourcompany.com"
              containerRef={modalRef.current}
              onChange={e => (
                updateZendeskAgentWorkspacePlatform({ auth: { email: e.target.value } })
              )}
              invalid={invalidZendeskCredentials}
            />
          </div>
          <InputRow
            // N.B.: "split" rows get an extra class name:
            className="ZendeskAgentWorkspace__Settings__cell"
            label="Zendesk API Token"
            tooltip={`You can get one of these from Zendesk in
          Settings > Channels > API > Token Access`}
            value={platform.getIn(["record", "auth", "apiToken"])}
            placeholder="e.g 1Jc9wXd9nyqAi187LWdJfnJjDY5NMr4ht4Y3nRTn"
            containerRef={modalRef.current}
            onChange={e => updateZendeskAgentWorkspacePlatform(
              { auth: { apiToken: e.target.value } }
            )}
            invalid={invalidZendeskCredentials}
          />
        </section>
      </div>
      <div
        className="Modal__bottom ZendeskAgentWorkspace__Settings__bottom"
      >
        <Button
          onClick={closeModal}
          text="Cancel"
          title="Cancel"
          customClassName="ZendeskAgentWorkspaceModal__cancel-button"
          light
        />
        <Button
          onClick={() => {
            const enabledPlatform = platform.setIn(["record", "enabled"], true);
            saveClientPlatform(enabledPlatform);
          }}
          text="Finish"
          title="Finish"
          disabled={isSaveDisabled}
          isLoading={platform.get("isLoading")}
          customClassName="ZendeskAgentWorkspaceModal__bottom__save"
        />
      </div>
    </>
  );
};

SettingsScreen.defaultProps = {
  errorInfo: null
};

SettingsScreen.propTypes = {
  platform: ImmutablePropTypes.map.isRequired,
  saveClientPlatform: PropTypes.func.isRequired,
  updateZendeskAgentWorkspacePlatform: PropTypes.func.isRequired,
  isSaveDisabled: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  modalRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.node })
  ]).isRequired,
  errorInfo: PropTypes.shape({
    type: PropTypes.string.isRequired,
    platform: PropTypes.string,
    message: PropTypes.string
  })
};

export default SettingsScreen;
